import {inject, Injectable} from "@angular/core";
import {
  AuthUser,
  createClient,
  SignInWithPasswordCredentials,
  SupabaseClient,
} from "@supabase/supabase-js";
import {environment} from "./../../environments/environment";
import {
  BehaviorSubject,
  catchError,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  of,
  tap,
  toArray,
} from "rxjs";
import {UserData} from "../features/auth/login/login.component";
import {
  AdditionalUserData,
  raw_app_meta_data,
  UserDB,
  Userrole,
} from "../shared/interfaces/user";
import {ProductionLine} from "../shared/interfaces/productionLine";
import {Workstation, WorkstationDB} from "../shared/interfaces/workstation";
import {Category} from "../shared/interfaces/category";
import {Solution} from "../shared/interfaces/solution";

import {
  DownloadResponse,
  MediaFile,
  MediaItem,
  SolutionMedia,
} from "../shared/interfaces/media";
import {UserroleDB} from "../shared/data-access/storage.service";
import {MalfunctionStatus, Report, WorkplaceReportFilterOptions} from "../shared/interfaces/report";
import {convertToUTCTime} from "../shared/helpers/dateTime/convertTime";
import {Escalation, EscalationRow, EscalationStatus, UpdateEscalation} from "../features/escalation/escalation.model";
import {AppUser} from "./user.service";
import {LocalStorageService} from "../shared/data-access/local-storage.service";
import {
  ReportDashboardData,
  ReportDashboardRequest,
  ReportDashboardResponse
} from "../features/report-dashboards/data-access/report-dashboard.service";
import {NotificationResponse, ReportDetails} from "./local-supabase-edge-function.service";
import {FilterParams} from "../shared/components/filter-interface/filter-interface.component";
import {StandstillStatistics} from "../features/workplace-report/workplace-report.component";
import {AiConfiguration, AiTestResult} from "../shared/interfaces/ai-configuration";

export enum Tables {
  Solution = "Solution",
  Workplace = "Workplace",
  Category = "Category",
  Malfunction = "Malfunction",
  Media = "Media",
  Report = "Report",
  Escalation = "Escalation",
  EscalationLog = "Escalation_log",
  Roles = "Roles",
  ProductionLine = "Production_line",
  User = "User",
  User_Roles = "User_Roles",
  Timeout = "Timeout",
  AiConfiguration = "ai_configuration",
  AiTestResults = "ai_test_results"
}

export interface SearchObject {
  relation: Tables,
  identifierKey: string,
  identifierValue: string
}

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private supabase: SupabaseClient;
  private localStorageService = inject(LocalStorageService)

  Userrole = Userrole;

  constructor() {
    console.log('applicationUrl from apiService', environment.applicationUrl)
    this.supabase = createClient(
      environment.supabaseUrl,
      environment.supabaseKey
    );
  }

  addItemToKnowledgeBase(relation: Tables, item: any): Observable<any> {
    return from(
      this.supabase
        .from(relation)
        .insert(item)
        .select()
        .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  updateItemInKnowledgeBase(relation: Tables, item: any): Observable<any> {
    return from(
      this.supabase.from(relation).update(item).eq("id", item.id).select()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  deleteItemFromKnowledgeBase(relation: Tables, id: string): Observable<any> {
    return from(
      this.supabase.from(relation).update({'is_deleted': true}).eq("id", id).select()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data[0] || [];
      })
    );
  }

  async addItem(relation: Tables, item: any) {
    const {data, error} = await this.supabase
      .from(relation)
      .insert(item)
      .select("*")
      .single();
    if (error) throw error;
    return data;
  }

  addAndReturnReport(report: Partial<Report>): Observable<Report> {
    return from(
      this.supabase
        .from(Tables.Report)
        .insert(report)
        .select()
        .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data;
      })
    );
  }

  updateAndReturnReport(report: Partial<Report>): Observable<Report> {
    return from(
      this.supabase
        .from(Tables.Report)
        .update(report)
        .eq("id", report.id)
        .select()
        .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data;
      })
    );
  }

  async addWorkstation(relation: Tables, item: WorkstationDB) {
    const {data, error} = await this.supabase
      .from(relation)
      .insert(item)
      .select("*, production_line(*)")
      .single();
    if (error) throw error;
    return data;
  }

  async updateWorkstation(relation: Tables, item: WorkstationDB) {
    const {data, error} = await this.supabase
      .from(relation)
      .update(item)
      .eq("id", item.id)
      .select("*, production_line(*)")
      .single();
    if (error) throw error;
    return data;
  }

  async updateItem<TInput extends {
    id: string | undefined
  }, TOutput = TInput>(relation: Tables, item: TInput): Promise<TOutput> {
    const {data, error} = await this.supabase
      .from(relation)
      .update(item)
      .eq("id", item.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async deleteItem(table: Tables, id: any) {
    if (table === Tables.User) {
      const placeholderUserId = environment.deletedSystemUserUuid;

      const {data, error} = await this.supabase.rpc('delete_user_and_reassign', {
        p_user_id: id,
        p_placeholder_user_id: placeholderUserId
      });

      if (error) throw error;
      return data;
    }

    const {data, error} = await this.supabase
      .from(table)
      .delete()
      .eq("id", id);
    if (error) throw error;
    return data;
  }

  countRows(
    relation: Tables,
    identifierName: string,
    identifierValue: string | number,
    secondIdentifierName?: string,
    secondIdentiferValue?: string | number
  ): Observable<number> {
    let query = this.supabase
      .from(relation)
      .select("*", {count: "exact", head: true})
      .eq(identifierName, identifierValue);
    if (secondIdentifierName && secondIdentiferValue)
      query.eq(secondIdentifierName, secondIdentiferValue);

    return from(query).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        } else if (response.count) console.log("count", response.count);
        return response.count || 0;
      })
    );
  }

  deleteMediaItem(path: string) {
    return from(
      this.supabase.from(Tables.Media).delete().eq("path", path)
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  updateMediaFilename(newFilename: string, path: string) {
    return from(
      this.supabase
        .from(Tables.Media)
        .update({filename: newFilename})
        .eq("path", path)
        .select()
        .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  deleteStorageItem(path: string) {
    return from(this.supabase.storage.from("Media").remove([`${path}`])).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  private set_claim = async (
    uid: string,
    claim: string,
    value: string | boolean
  ) => {
    const {data, error} = await this.supabase.rpc("set_claim", {
      uid,
      claim,
      value,
    });
    if (error) {
      console.error(error);
    }
    return data;
  };

  getUserData() {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;

  }

  getUsers(): Observable<UserDB[]> {
    return from(this.getAllItems(Tables.User)).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  getSingleUserById(userUuid: string): Observable<AdditionalUserData> {
    return from(
      this.supabase
        .from(Tables.User)
        .select("username, first_name, last_name, require_password_update")
        .eq("id", userUuid)
        .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  getProductionLinesAdmin(): Observable<ProductionLine[]> {
    return from(this.getAllItems(Tables.ProductionLine)).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  getWorkstationsAdmin(): Observable<Workstation[]> {
    return from(
      this.supabase.from(Tables.Workplace).select("*, production_line(*)")
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  createNewUser(user: UserDB): Observable<UserDB> {
    return from(
      this.supabase.from(Tables.User).insert(user).select("*").single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  clearUserData() {
    localStorage.removeItem("userData");
    localStorage.removeItem(`sb-${environment.supabaseProjectId}-auth-token`);
    localStorage.removeItem('user');
    localStorage.removeItem('session_expiration_time')
    this.supabase.auth.signOut();
  }

  updateUser(updatedUser: UserDB): Observable<UserDB> {
    this.set_claim(
      updatedUser.id!,
      "userrole",
      updatedUser.raw_app_meta_data.userrole
    );
    if (
      updatedUser.raw_app_meta_data.userrole === Userrole.Teamleiter ||
      updatedUser.raw_app_meta_data.userrole === Userrole.Schichtleiter
    ) {
      console.log("set claim", updatedUser.raw_app_meta_data.productionLine);
      this.set_claim(
        updatedUser.id!,
        "productionLine",
        updatedUser.raw_app_meta_data.productionLine!
      );
    }
    this.set_claim(
      updatedUser.id!,
      "useractive",
      updatedUser.raw_app_meta_data.useractive
    );

    return from(
      this.supabase
        .from(Tables.User)
        .update(updatedUser)
        .eq("id", updatedUser.id)
        .select("*")
        .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  async initializePasswordReset(userId: string, password: string) {
    /* console.log(
      `password reset initialized for user ${userId}. New password: ${password}`
    ); */
    await this.supabase.rpc("reset_user_login", {
      user_id: userId,
      password: password,
    });
    await this.supabase
      .from(Tables.User)
      .update({require_password_update: true})
      .eq("id", userId);
    alert("Passwort zurückgesetzt");
  }

  async updatePassword(password: string, userId: string): Promise<void> {
    // Erst Passwort aktualisieren
    const {error: authError} = await this.supabase.auth.updateUser({password: password});

    if (authError) throw authError;

    // Nur wenn Passwort-Update erfolgreich war, Datenbank-Flag aktualisieren
    const {error: dbError} = await this.supabase
      .from(Tables.User)
      .update({require_password_update: false})
      .eq("id", userId);

    if (dbError) throw dbError;
  }

  async signIn(credentials: UserData) {
    // check if email or username was provided

    let userData: SignInWithPasswordCredentials = {
      email: credentials.username.includes("@")
        ? credentials.username
        : credentials.username + environment.authEmail,
      password: credentials.password,
    };
    return await this.supabase.auth.signInWithPassword(userData);
  }

  getSessionTimeout(): Observable<number> {
    return from(this.getAllItems(Tables.Timeout).single()).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data?.session_timeout || null;
      })
    );
  }

  getProductionLines() {
    return this.getAllItems(Tables.ProductionLine);
  }

  async getUserRoles(): Promise<UserroleDB[]> {
    const userroles = await this.getAllItems(Tables.Roles);
    return userroles.data || [];
  }

  getWorkplacesByProductionLine(productionLineId: string) {
    return this.getAllItems(Tables.Workplace).eq(
      "production_line",
      productionLineId
    );
  }

  getChildrenByParent(tableName: Tables, parent: string, parentId: string) {
    return from(this.supabase.from(tableName).select("*").eq(parent, parentId).eq('is_deleted', false));
  }

  getActiveEscalation(searchObject: SearchObject): Observable<any> {
    return from(this.supabase
      .from(searchObject.relation)
      .select('*')
      .eq(searchObject.identifierKey, searchObject.identifierValue)
      .neq('status', 3)
      .order('created_at', {ascending: false})
      .limit(1)
      .single()
    )
      .pipe(map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || null;
      }))
  }

  getItemByIdentifier(searchObject: SearchObject): Observable<any> {
    return from(this.supabase
      .from(searchObject.relation)
      .select('*').eq(searchObject.identifierKey, searchObject.identifierValue)
      .single()
    )
      .pipe(map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || null;
      }))
  }

  getWorkplaceById(id: string) {
    return from(
      this.supabase
        .from(Tables.Workplace)
        .select("*, production_line(id, name)")
        .eq("id", id)
        .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  getWorkplacesForProductionLine(productionLineId: string) {
    return from(
      this.getChildrenByParent(
        Tables.Workplace,
        "production_line",
        productionLineId
      )
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  getCategoriesForWorkplace(workplaceId: string): Observable<Category[]> {
    return from(
      this.getChildrenByParent(Tables.Category, "workplace", workplaceId)
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  getMalfunctionsForCategory(categoryId: string) {
    return from(
      this.getChildrenByParent(Tables.Malfunction, "category", categoryId)
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  getSolutionsForMalfunction(malfunctionId: string) {
    return from(
      this.getChildrenByParent(Tables.Solution, "malfunction", malfunctionId)
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  /* getLatestReports(): Observable<any[]> {
    return from(
      this.supabase
        .from(Tables.Report)
        .select("*, malfunction(*)")
        .order("created_at", { ascending: false })
        .limit(5)
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  } */

  getSolutionByIdAsObservable(solutionId: string): Observable<Solution> {
    return from(
      this.supabase
        .from(Tables.Solution)
        .select("*")
        .eq("id", solutionId)
        .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  getFilteredReports({
                       rangeStart,
                       rangeEnd,
                       workplaceFilter,
                       productionLineFilter,
                       statusFilter,
                       searchText,
                       startDate,
                       endDate,
                       startTime,
                       endTime,
                       minDuration,
                       maxDuration,
                       statusLimits
                     }: WorkplaceReportFilterOptions = {}): Observable<{
    reportData: Report[];
    totalCount: number;
  }> {
    return from(
      this.supabase.rpc("get_workplace_report_data", {
        range_start: rangeStart ? rangeStart : 0,
        range_end: rangeEnd ? rangeEnd : null,
        workplace_filter: workplaceFilter ? workplaceFilter : null,
        production_line_filter: productionLineFilter
          ? productionLineFilter
          : null,
        status_filter: statusFilter ? statusFilter : null,
        search_term: searchText,
        startdate: startDate ? startDate : null,
        enddate: endDate ? endDate : null,
        starttime: startTime ? convertToUTCTime(startTime) : null,
        endtime: endTime ? convertToUTCTime(endTime) : null,
        minduration: minDuration ? minDuration : null,
        maxduration: maxDuration ? maxDuration : null,
        status_limits: statusLimits ? statusLimits : null,
      })
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        } else {
          return response.data[0] || [];
        }
      })
    );
  }

  calculateDurationExcludingClosure(startTime: Date, endTime: Date): Observable<number> {
    return from(this.supabase.rpc('calculate_duration_excluding_closure', {
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString()
    })).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        } else {
          return response.data;
        }
      })
    )
  }

  getChartData(params: {
    filterParams: FilterParams,
    workplaceFilter: string | undefined | null,
    productionLineFilter: string | undefined | null
  }): Observable<StandstillStatistics> {
    return from(
      this.supabase.rpc('analyze_resolution_times', {
        startdate: params.filterParams.startDate,
        enddate: params.filterParams.endDate,
        workplace_id: params.workplaceFilter,
        production_line_id: params.productionLineFilter
      })
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        } else {
          return response.data || [];
        }
      })
    )
  }

  getActiveEscalations(user: AppUser, productionLineFilter: string | null = null): Observable<Report[]> {
    return from(
      this.supabase.rpc('get_escalated_reports', {
        user_id: user.id,
        userrole: user.app_metadata['userrole'],
        production_line: productionLineFilter
      })
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        } else {
          return response.data || [];
        }
      })
    )
  }

  searchKnowledgeBase(searchTerm: string, productionLineFilter: string | null) {
    console.log('calling database function', searchTerm, productionLineFilter)
    return from(this.supabase.rpc('search_knowledge_database', {
        search_term: searchTerm,
        production_line_filter: productionLineFilter
      })
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        } else {
          console.log(response);
          return response.data || [];
        }
      })
    );
  }

  getMediaFilesForSolution(solutionId: string): Observable<MediaFile[]> {
    return from(this.getAllItems(Tables.Media).eq("solution", solutionId)).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return (
          response.data?.map((media) => ({
            id: media.id,
            name: media.filename,
            bucket: media.bucket,
            path: media.path,
          })) || []
        );
      }),
      mergeMap((mediaItems: MediaItem[]) =>
        mediaItems.length
          ? from(mediaItems).pipe(
            mergeMap((mediaItem: MediaItem) =>
              this.fetchMediaFileFromStorage(mediaItem)
            ),
            filter((mediaFile): mediaFile is MediaFile => mediaFile !== null),
            toArray()
          )
          : of([])
      ),
      catchError((error) => {
        console.error("Error fetching media files:", error);
        return of([]);
      })
    );
  }

  fetchMediaFileFromStorage(mediaItem: MediaItem) {
    return from(
      this.supabase.storage.from(mediaItem.bucket).download(mediaItem.path)
    ).pipe(
      map((response: DownloadResponse) => {
        if (response.error !== null) {
          console.error(
            `Error downloading file ${mediaItem.id}:`,
            response.error
          );
          return null;
        }
        return {
          id: mediaItem.id,
          name: mediaItem.name,
          file: response.data,
          path: mediaItem.path,
        };
      }),
      catchError((error) => {
        console.error(`Error fetching file ${mediaItem.id}:`, error);
        return of(null);
      })
    );
  }

  getMediaBySolution(solutionId: string) {
    return this.getAllItems(Tables.Media).eq("solution", solutionId);
  }

  getReportNumber() {
    return this.supabase
      .from(Tables.Report)
      .select("current_number")
      .order("current_number", {ascending: false})
      .limit(1)
      .maybeSingle();
  }

  getCurrentIncidentNumber(workstationUuid: string): Observable<number> {
    return from(
      this.supabase
        .from(Tables.Report)
        .select("*", {count: "exact", head: true})
        .eq("workplace", workstationUuid)
        .neq("incident_id", "")
        .filter("created_at", "gte", new Date().toISOString().split("T")[0])
        .filter(
          "created_at",
          "lt",
          new Date(new Date().getTime() + 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0]
        )
        .filter("status", "gt", MalfunctionStatus.RECORDED)
        .filter("status", "lt", MalfunctionStatus.CANCELED)
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        } else {
          // console.log(response);
          return response.count!;
        }
      })
    );
  }

  getAllItems(table: Tables) {
    if (table === Tables.User) {
      return this.supabase
        .from(table)
        .select("*", {count: "exact"})
        .eq("is_deleted", false)
        .neq('id', environment.deletedSystemUserUuid);
    }
    return this.supabase.from(table).select("*");
  }

  addAndReturnItem<TInput, TOutput = TInput>(relation: Tables, item: TInput): Observable<TOutput> {
    return from(
      this.supabase
        .from(relation)
        .insert(item)
        .select()
        .single()
    ).pipe(map((response) => {
      if (response.error) {
        throw new Error(response.error.message);
      } else {
        console.log(response);
        return response.data
      }
    }))
  }

  updateAndReturnItem(relation: Tables, item: Partial<Report>, identifier: {
    key: string,
    value: string
  }): Observable<Report> {
    return from(
      this.supabase
        .from(relation)
        .update(item)
        .eq(identifier.key, identifier.value)
        .select()
        .single()
    ).pipe(map((response) => {
      if (response.error) {
        throw new Error(response.error.message);
      } else {
        console.log(response);
        return response.data
      }
    }))
  }

  updateEscalation(escalationUpdate: UpdateEscalation): Observable<EscalationRow> {
    let query
    if (escalationUpdate.id) {
      return from(this.supabase
        .from(Tables.Escalation)
        .update(escalationUpdate)
        .eq('id', escalationUpdate.id)
        .select()
        .single()
      ).pipe(
        map((response) => {
          if (response.error) {
            throw new Error(response.error.message);
          }
          return response.data;
        })
      );

    } else {
      return from(this.supabase
        .from(Tables.Escalation)
        .update(escalationUpdate)
        .eq('report', escalationUpdate.report)
        .neq('status', EscalationStatus.COMPLETED)
        .select()
        .single())
        .pipe(
          map((response) => {
            if (response.error) {
              throw new Error(response.error.message);
            }
            return response.data;
          })
        );
    }
  }

  async insertItem(itemName: Tables, item: any) {
    return this.supabase.from(itemName).insert(item).select();
  }

  updateName(itemName: Tables, id: string, name: string) {
    return this.supabase
      .from(itemName)
      .update({name: name})
      .eq("id", id)
      .select()
      .maybeSingle();
  }

  async updateWorkstationPosition(id: string, newPosition: number) {
    // console.log(`update position to ${newPosition} for workstation ${id}`);
    const {data, error} = await this.supabase
      .from(Tables.Workplace)
      .update({position: newPosition})
      .eq("id", id)
      .select()
      .single();
    if (data) {
      // console.log(data);
    } else {
      console.error(error);
    }
  }

  async updateSessionTimeout(sessionTimeout: number) {
    let response = await this.supabase
      .from(Tables.Timeout)
      .update({session_timeout: sessionTimeout})
      .eq("id", 1)
      .select()
      .single();
    return response.data;
  }

  getActiveReports() {
    return this.supabase
      .from(Tables.Report)
      .select("id,category(id,name),workplace(id,name)")
      .eq("status", 3);
  }

  getMediaCount(id: string) {
    return this.supabase
      .from(Tables.Media)
      .select("*", {count: "exact", head: true})
      .eq("solution", id);
  }

  getReportById(id: string): Observable<Report> {
    return from(this.supabase
      .from("Report")
      .select(
        "*,category(*),malfunction(*),workplace(*, production_line(*)),solution(*)"
      )
      .eq("id", id)
      .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data;
      })
    );
  }

  getReportsByIdentifier(searchObject: SearchObject): Observable<Report[]> {
    return from(this.supabase
      .from(Tables.Report)
      .select('*')
      .eq(searchObject.identifierKey, searchObject.identifierValue)
      .or('status.in.(1,2,3)')
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data;
      })
    )
  }

  getReportsBySolutionId(solutionId: string): Observable<Report[]> {
    return this.getReportsByIdentifier({
      relation: Tables.Report,
      identifierKey: 'solution',
      identifierValue: solutionId
    })
  }

  getReportsByMalfunctionId(malfunctionId: string): Observable<Report[]> {
    return this.getReportsByIdentifier({
      relation: Tables.Report,
      identifierKey: 'malfunction',
      identifierValue: malfunctionId
    });
  }


  getUserName(id: string) {
    return from(
      this.supabase
        .from(Tables.User)
        .select("first_name, last_name")
        .eq("id", id)
        .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        console.log(response);
        return response.data || [];
      })
    );
  }

  getReportsByWorkplace(workplaceId: string | null) {
    return this.supabase
      .from("Report")
      .select("*,category(*),malfunction(*),workplace(*)")
      .eq("workplace", workplaceId)
      .neq("status", 0)
      .neq("status", 1)
      .order("created_at", {ascending: false});
  }

  solveReport(id: string, solutionId: string, username: string) {
    // console.log(id, solutionId, username);
    let now = new Date();
    return this.supabase
      .from("Report")
      .update({
        status: 2,
        solution: solutionId,
        solved_on: now,
        solved_by: username,
      })
      .eq("id", id)
      .single();
  }

  solveActiveReport(id: string, userEmail: string, solutionId: string) {
    let now = new Date();
    // console.log("SolveActiveReport ", id);
    return this.supabase
      .from("Report")
      .update({
        status: 2,
        solved_on: now,
        solved_by: userEmail,
        solution: solutionId,
      })
      .eq("id", id)
      .single();
  }

  uploadMediaFile(file: File, filename: string, solutionId: string) {
    console.log("uploading file", filename, file);
    this.supabase.storage.from("Media").upload(file.name, file);
    return from(
      this.insertItem(Tables.Media, {
        solution: solutionId,
        filename: filename,
        path: file.name,
      })
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data || [];
      })
    );
  }

  async uploadFile(file: File, solutionId: string) {
    const filePath = `${file.name}`;
    const {data, error} = await this.supabase.storage
      .from("Media")
      .upload(filePath, file);
    if (error) {
      throw new Error(error.message);
    } else {
      let media = {
        solution: solutionId,
        path: filePath,
      };
      return this.insertItem(Tables.Media, media);
    }
  }

  async getMediaFile(bucket: string, path: string): Promise<Blob> {
    const {data, error} = await this.supabase.storage
      .from(bucket)
      .download(path);
    if (error) {
      throw new Error("Error fetching media file: " + error.message);
    }
    return data;
  }

  getDashboardData(dashboardRequest: ReportDashboardRequest): Observable<ReportDashboardResponse> {
    switch (dashboardRequest.type) {
      case "daily":
        return from(this.supabase.rpc('get_daily_reports', {production_line_id: dashboardRequest.productionLine}))
          .pipe(
            map((response) => {
              if (response.error) {
                throw new Error(response.error.message);
              }
              return response.data as ReportDashboardResponse;
            })
          );
      case "weekly":
        return from(this.supabase.rpc('get_weekly_reports', {production_line_id: dashboardRequest.productionLine}))
          .pipe(
            map((response) => {
              if (response.error) {
                throw new Error(response.error.message);
              }
              return response.data as ReportDashboardResponse;
            })
          );
    }
  }

  sendEscalationEmail(targetRole: Userrole, reportDetails: ReportDetails): Observable<NotificationResponse> {
    return from(this.supabase.functions.invoke('send-email', {
      body: {
        targetRole: targetRole,
        reportDetails: reportDetails
      }
    })).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data;
      })
    );
  }

  deleteReport(id: string): Observable<Report> {
    return this.updateAndReturnItem(Tables.Report, {status: MalfunctionStatus.DELETED}, {key: 'id', value: id})
  }

  getAiConfigurations(): Observable<AiConfiguration[]> {
    return from(this.getAllItems(Tables.AiConfiguration)).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message)
        }
        if (!response.data || response.data.length === 0) {
          throw new Error('Keine Konfiguration gefunden');
        }
        return response.data;
      })
    )
  }

  updateAiConfiguration(config: AiConfiguration): Observable<AiConfiguration> {
    console.log('updating ai configuration', config);
    return from(
      this.supabase
        .from(Tables.AiConfiguration)
        .update(config)
        .eq("id", config.id)
        .select()
        .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data;
      })
    );
  }

  createAiConfiguration(config: Omit<AiConfiguration, "id">): Observable<AiConfiguration> {
    return from(
      this.supabase
        .from(Tables.AiConfiguration)
        .insert(config)
        .select()
        .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data;
      })
    );
  }

  saveAiTestResult(testResult: Omit<AiTestResult, "id">): Observable<AiTestResult> {
    return from(
      this.supabase
        .from(Tables.AiTestResults)
        .insert(testResult)
        .select()
        .single()
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        return response.data;
      })
    );
  }

  async getGptResponse(input: string): Promise<string> {
    const response = await fetch('http://localhost/chat', {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'X-Api-Key': 'Qundis2024'},
      body: JSON.stringify({content: input.replace(/<[^>]+>/g, '')})
    });

    if (!response.ok) {
      const details = await response.text().catch(() => '');
      throw new Error(`AI-Service Fehler ${response.status} ${details}`);
    }

    const data: { response?: string } = await response.json();
    if (!data?.response) {
      throw new Error('Leere KI-Antwort');
    }

    return data.response;
  }
}
